import { cn } from '@/lib/utils';
import {
  MATRIX_PIVOT_SETUP_FIELD,
  type MatrixPivot,
  SINGULARITY_STEP_NAMES,
  type SingularityLevier,
} from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';

export interface SetupStepRowProps {
  /** The setup field the row is titled by, and the one highlighted once expanded. */
  pivot: MatrixPivot;
  /** One of the company's 6 setup steps. */
  step: SingularityLevier;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

type DetailField = Exclude<keyof SingularityLevier, 'order'>;

/** The 8 fields, in their fixed order whatever the pivot. */
const DETAIL_FIELDS: readonly { key: DetailField; label: string }[] = [
  { key: 'driver', label: 'Driver' },
  { key: 'levierImplication', label: "Levier d'implication" },
  { key: 'detailImplication', label: "Détail de l'implication" },
  { key: 'principeAction', label: "Principe d'action" },
  { key: 'methode', label: 'Méthode' },
  { key: 'levierValeur', label: 'Levier de valeur' },
  { key: 'detailValeur', label: 'Détail de la valeur' },
  { key: 'effetProduit', label: 'Effet produit' },
];

const NOT_FILLED = 'Non renseigné';

function filled(value: string | null): value is string {
  return (value?.trim() ?? '') !== '';
}

/**
 * One setup step, titled by the matrix's pivot (issue #16).
 *
 * Collapsed: an over-title « Étape N - <nom d'étape> » and, as the title, the
 * step's value for the pivot. Expanded: all 8 fields, the pivot's one
 * highlighted, a blank one reading « Non renseigné ».
 *
 * Controlled, and free of any wizard state, so the Cadrage tab's setup cards
 * render the same row. The title is never blank in the wizard, whose selector
 * refuses a pivot missing on a step; a frozen matrix whose setup was emptied
 * since can still show one, hence the same « Non renseigné » fallback.
 */
export function SetupStepRow({ pivot, step, expanded, onExpandedChange }: SetupStepRowProps) {
  const pivotField = MATRIX_PIVOT_SETUP_FIELD[pivot];
  const title = step[pivotField];
  const stepName = SINGULARITY_STEP_NAMES[step.order - 1] ?? '';

  return (
    <div className="rounded-lg border-2 border-gray-100 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        {expanded ? <Icon name="expand_more" size={16} /> : <Icon name="chevron_right" size={16} />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-caption text-gray-500 tracking-wide">
            Étape {step.order} - {stepName}
          </p>
          <p
            className={cn(
              'truncate font-semibold text-body',
              !filled(title) && 'font-normal text-gray-400 italic',
            )}
          >
            {filled(title) ? title : NOT_FILLED}
          </p>
        </div>
      </button>
      {expanded && (
        <dl className="grid grid-cols-1 gap-3 border-gray-100 border-t-2 px-4 py-4 sm:grid-cols-2">
          {DETAIL_FIELDS.map(({ key, label }) => {
            const value = step[key];
            const isPivot = key === pivotField;
            return (
              <div
                key={key}
                data-pivot={isPivot || undefined}
                className={cn(isPivot && '-mx-2 -my-1 rounded-md bg-bleu-nuit-tint px-2 py-1')}
              >
                <dt
                  className={cn(
                    'flex items-center gap-2 font-semibold text-caption uppercase tracking-wide',
                    isPivot ? 'text-bleu-nuit' : 'text-gray-500',
                  )}
                >
                  {label}
                  {isPivot && (
                    <span className="rounded-full bg-bleu-nuit px-2 py-px font-bold text-micro text-white normal-case tracking-normal">
                      Pivot
                    </span>
                  )}
                </dt>
                <dd className={cn('mt-0 text-small', !filled(value) && 'text-gray-400 italic')}>
                  {filled(value) ? value : NOT_FILLED}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}
